// src/components/FieldRenderer.jsx
// Renders a single field based on its type, mirroring design.asp exactly.
// Types: C/I/R (text), T (textarea), D/H (date/datetime), O (select), L (link), Q (query button), S (signoff)

import React, { useState } from 'react';
import { documentApi } from '../services/api';
import { useTexts, TXT } from '../context/TextContext';
import { useToast } from '../context/ToastContext';

// Separate component for signoff fields (needs useState)
function SignoffField({ field, docId, onSignoff }) {
  const { label, value, predef } = field;
  const { t } = useTexts();
  const { showToast } = useToast();
  const [signing, setSigning] = useState(false);
  const [signed, setSigned]   = useState(!!value);

  const handleChange = async (e) => {
    if (!e.target.checked) return;
    setSigning(true);
    try {
      await documentApi.signoff(docId, predef);
      setSigned(true);
      if (onSignoff) onSignoff();
    } catch (err) {
      e.target.checked = false;
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    } finally {
      setSigning(false);
    }
  };

  if (signed) return null;

  return (
    <div className="col-sm-6 mt-2" id="fldsignoff">
      <div className="container">
        <div className="row campo" style={{ borderBottom: '1px solid' }}>
          <div className="col-sm-12 mb-2 valor">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`signoff_${field.id}`}
                checked={signed}
                disabled={signed || signing}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor={`signoff_${field.id}`}>
                {signing ? 'Firmando...' : (signed ? '' : label)}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QueryField({ field, docId, readOnly, onChange }) {
  const { t } = useTexts();
  const { label, id, name, tSize, vSize, value } = field;
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null); // { columns, rows }
  const [error,   setError]   = useState(null);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await documentApi.runQuery(docId, id);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al ejecutar la consulta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-sm-12 mt-2">
      <div className="container">
        <div className="row campo" style={{ borderBottom: '1px solid' }}>
          {tSize !== 0 && (
            <div className="col-sm-2 mb-2">{label}</div>
          )}
          <div className={`col-sm-${tSize !== 0 ? 10 : 12} mb-2`}>
            {!readOnly && (
              <textarea
                className="form-control mb-2"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                value={value || ''}
                onChange={e => onChange && onChange(id, name, e.target.value)}
              />
            )}
            <button
              className="button mt-1 mb-1 color1"
              type="button"
              disabled={loading}
              onClick={handleRun}
            >
              <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-list'} fa-1x`} />
              &nbsp;{loading ? '…' : t(TXT.RUN_QUERY, 'Consultar')}
            </button>

            {error && (
              <div className="text-danger small mt-1">{error}</div>
            )}

            {result && result.rows.length === 0 && (
              <div className="text-muted small mt-2">Sin resultados.</div>
            )}

            {result && result.rows.length > 0 && (
              <div className="mt-2" style={{ overflowX: 'auto' }}>
                <table className="table table-sm table-bordered table-hover" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-light">
                    <tr>
                      {result.columns.map(col => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr key={i}>
                        {result.columns.map(col => (
                          <td key={col}>{row[col] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FieldRenderer({ field, onChange, readOnly, docId, onSignoff }) {
  const { t } = useTexts();
  const { id, name, type, label, tSize, vSize, options, value, required } = field;

  // Don't render if both tSize and vSize are 0 (hidden field)
  if (!tSize && !vSize) return null;

  const inputStyle = {
    border: 'none',
    outline: 0,
    minWidth: '100%',
    background: 'transparent',
  };

  const handleChange = (val) => {
    if (onChange) onChange(id, name, val);
  };

  const renderInput = () => {
    switch (type) {
      // ----------------------------------------------------------------
      // C = text, I = integer, R = real/decimal
      // ----------------------------------------------------------------
      case 'C':
      case 'I':
      case 'R':
        return (
          <input
            style={inputStyle}
            type="text"
            name={name}
            defaultValue={value || ''}
            readOnly={readOnly}
            onChange={e => handleChange(e.target.value)}
              />
        );

      // ----------------------------------------------------------------
      // T = textarea
      // ----------------------------------------------------------------
      case 'T':
        return (
          <textarea
            style={{ ...inputStyle, resize: 'vertical' }}
            name={name}
            defaultValue={value || ''}
            readOnly={readOnly}
            rows={6}
            onChange={e => handleChange(e.target.value)}
          />
        );

      // ----------------------------------------------------------------
      // D = date, H = datetime
      // ----------------------------------------------------------------
      case 'D':
        return (
          <input
            style={inputStyle}
            type="date"
            name={name}
            defaultValue={value || ''}
            readOnly={readOnly}
            onChange={e => handleChange(e.target.value)}
          />
        );

      case 'H':
        return (
          <input
            style={inputStyle}
            type="datetime-local"
            name={name}
            defaultValue={value || ''}
            readOnly={readOnly}
            onChange={e => handleChange(e.target.value)}
          />
        );

      // ----------------------------------------------------------------
      // O = select/dropdown from WHL_POS_POSIB
      // ----------------------------------------------------------------
      case 'O':
        return (
          <select
            style={{ ...inputStyle, fontSize: '0.9rem' }}
            name={name}
            defaultValue={value || ''}
            disabled={readOnly}
            onChange={e => handleChange(e.target.value)}
          >
            <option value=""></option>
            {(options || []).map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        );

      // ----------------------------------------------------------------
      // L = link — text input + view button
      // ----------------------------------------------------------------
      case 'L':
        return (
          <div className="d-flex align-items-center gap-1" style={{ width: '100%' }}>
            <input
              style={{ ...inputStyle, minWidth: 0, flex: 1 }}
              type="text"
              name={name}
              defaultValue={value || ''}
              readOnly={readOnly}
              onChange={e => handleChange(e.target.value)}
            />
            {value && (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-light btn-sm flex-shrink-0"
                title={t(TXT.VIEW, 'Ver')}
              >
                <i className="fa-solid fa-eye fa-1x" />
              </a>
            )}
          </div>
        );

      // ----------------------------------------------------------------
      // Q = handled by QueryField component, not renderInput
      // ----------------------------------------------------------------
      case 'Q':
        return null;

      // ----------------------------------------------------------------
      // S = signoff — handled by outer component, not renderInput
      // ----------------------------------------------------------------
      case 'S':
        return null;

      default:
        return (
          <input
            style={inputStyle}
            type="text"
            name={name}
            defaultValue={value || ''}
            readOnly={readOnly}
            onChange={e => handleChange(e.target.value)}
          />
        );
    }
  };

  // S type — rendered as separate component to allow hooks
  if (type === 'S') {
    return <SignoffField field={field} docId={docId} onSignoff={onSignoff} />;
  }

  // Q type — delegate to QueryField
  if (type === 'Q') {
    return <QueryField field={field} docId={docId} readOnly={readOnly} onChange={onChange} />;
  }

  // Standard field layout — mirrors design.asp col-sm-6 structure
  return (
    <div className="col-sm-6 mt-2">
      <div className="container">
        <div className="row campo" style={{ borderBottom: '1px solid' }}>
          {tSize !== 0 && (
            <div className="col-sm-3 mb-2">
              {label}
                  </div>
          )}
          {vSize !== 0 && (
            <div className="col-sm-9 mb-2 valor">
              {renderInput()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
